import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

// Import extracted components
import {
  PLReport,
  ExposureReport,
  PerformanceReport,
  LegalReport
} from '../components/reports';
import BouncePnlReport from '../components/reports/BouncePnlReport';
import AnalyticsExtras from '../components/reports/AnalyticsExtras';
import ClientReport from '../components/reports/ClientReport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const { getAuthHeaders, user } = useAuth();
  // Client Report (#34) is admin-only end to end: the endpoints 403 non-admins and the tab is not rendered.
  const isAdmin = !!user?.roles?.includes('admin');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pl');
  
  // Filters
  const [dateRange, setDateRange] = useState('ytd');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedFunder, setSelectedFunder] = useState('all');
  const [selectedBroker, setSelectedBroker] = useState('all');
  
  // Data
  const [plData, setPlData] = useState(null);
  const [exposureData, setExposureData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [funders, setFunders] = useState([]);
  const [brokers, setBrokers] = useState([]);

  const fetchFilters = useCallback(async () => {
    try {
      const [fundersRes, brokersRes] = await Promise.all([
        axios.get(`${API}/contacts?type=Funder`, { headers: getAuthHeaders() }),
        axios.get(`${API}/contacts?type=Broker`, { headers: getAuthHeaders() })
      ]);
      setFunders(fundersRes.data || []);
      setBrokers(brokersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch filters:', error);
    }
  }, [getAuthHeaders]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_range: dateRange,
        start_date: startDate,
        end_date: endDate,
        funder_id: selectedFunder !== 'all' ? selectedFunder : undefined,
        broker_id: selectedBroker !== 'all' ? selectedBroker : undefined
      };

      // Load the three reports independently so one failing endpoint
      // doesn't blank the whole page — render whatever succeeds.
      const [plRes, exposureRes, perfRes] = await Promise.allSettled([
        axios.get(`${API}/reports/profit-loss`, { headers: getAuthHeaders(), params }),
        axios.get(`${API}/reports/exposure`, { headers: getAuthHeaders(), params }),
        axios.get(`${API}/reports/performance`, { headers: getAuthHeaders(), params })
      ]);

      const failed = [];
      if (plRes.status === 'fulfilled') setPlData(plRes.value.data);
      else { failed.push('P&L'); console.error('P&L report failed:', plRes.reason); }
      if (exposureRes.status === 'fulfilled') setExposureData(exposureRes.value.data);
      else { failed.push('Exposure'); console.error('Exposure report failed:', exposureRes.reason); }
      if (perfRes.status === 'fulfilled') setPerformanceData(perfRes.value.data);
      else { failed.push('Performance'); console.error('Performance report failed:', perfRes.reason); }

      if (failed.length === 3) {
        toast.error('Failed to load reports');
      } else if (failed.length > 0) {
        toast.error(`Couldn't load: ${failed.join(', ')} report${failed.length > 1 ? 's' : ''}`);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, dateRange, startDate, endDate, selectedFunder, selectedBroker]);

  useEffect(() => {
    fetchFilters();
    fetchReports();
  }, [fetchFilters, fetchReports]);

  const handleExport = async (reportType, format) => {
    try {
      const params = {
        date_range: dateRange,
        start_date: startDate,
        end_date: endDate,
        funder_id: selectedFunder !== 'all' ? selectedFunder : undefined,
        broker_id: selectedBroker !== 'all' ? selectedBroker : undefined,
        format
      };

      const response = await axios.get(`${API}/reports/${reportType}/export`, {
        headers: getAuthHeaders(),
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${reportType} report exported successfully`);
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  if (loading && !plData) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600">Comprehensive financial reporting and performance analytics</p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label>Date Range</Label>
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mtd">Month to Date</SelectItem>
                    <SelectItem value="qtd">Quarter to Date</SelectItem>
                    <SelectItem value="ytd">Year to Date</SelectItem>
                    <SelectItem value="last30">Last 30 Days</SelectItem>
                    <SelectItem value="last90">Last 90 Days</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {dateRange === 'custom' && (
                <>
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </>
              )}

              <div>
                <Label>Funder</Label>
                <Select value={selectedFunder} onValueChange={setSelectedFunder}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Funders</SelectItem>
                    {funders.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Broker</Label>
                <Select value={selectedBroker} onValueChange={setSelectedBroker}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Brokers</SelectItem>
                    {brokers.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-6' : 'grid-cols-5'}`}>
            <TabsTrigger value="pl">P&L Report</TabsTrigger>
            <TabsTrigger value="exposure">Exposure Report</TabsTrigger>
            <TabsTrigger value="performance">Performance Analytics</TabsTrigger>
            <TabsTrigger value="legal" data-testid="reports-legal-tab">Legal</TabsTrigger>
            <TabsTrigger value="bounce-pnl">Bounce P&amp;L</TabsTrigger>
            {isAdmin && <TabsTrigger value="client" data-testid="reports-client-tab">Client Report</TabsTrigger>}
          </TabsList>

          {/* P&L Report Tab - Using extracted component */}
          <TabsContent value="pl">
            <PLReport plData={plData} onExport={handleExport} />
          </TabsContent>

          {/* Exposure Report Tab - Using extracted component */}
          <TabsContent value="exposure">
            <ExposureReport exposureData={exposureData} onExport={handleExport} />
          </TabsContent>

          {/* Performance Analytics Tab - Using extracted component */}
          <TabsContent value="performance">
            <PerformanceReport performanceData={performanceData} />
            {/* Phase 6 Part 2c: status distribution, bounce trend, top syndicators (admin-only endpoints). */}
            {isAdmin && <AnalyticsExtras />}
          </TabsContent>

          {/* Legal Tab - Lawyer collections, settlements, write-offs */}
          <TabsContent value="legal">
            <LegalReport />
          </TabsContent>

          {/* Bounce P&L — fees charged vs real ACH return cost */}
          <TabsContent value="bounce-pnl">
            <BouncePnlReport dateRange={dateRange} startDate={startDate} endDate={endDate} />
          </TabsContent>

          {/* Client Report (#34) — one party's full picture: merchant / ISO / syndicator. Has its own
              search + period; ignores the page filters above. Read-only. */}
          {isAdmin && (
            <TabsContent value="client">
              <ClientReport />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
};

export default Reports;
